import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {CodaltComponent} from '../codalt.component';
import {Platform} from '../classes/platform';
import {PlatformService} from '../services/platform.service';
import {level, level as LevelEnum} from '../classes/checkpoint';
import {NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {PlatformLevelStatusPipe} from '../pipes/platform-level-status.pipe';
import {BreadcrumbComponent} from '../services/breadcrumb/breadcrumb.component';
import {Routenames} from '../classes/route-name.enum';
import {UrlPrefixStrippingPipe} from '../pipes/url-prefix-stripping.pipe';
import {HasFeaturePipe} from '../pipes/has-feature.pipe';
import {SslValidUntilPipe} from '../pipes/ssl-valid-until.pipe';
import {DaysFromNowPipe} from '../pipes/days-from-now.pipe';
import {MinDatePipe} from '../generic/pipes/min-date.pipe';
import {DatePipe} from '@angular/common';
import {Server} from '../classes/server';
import {ServerService} from '../services/server.service';
import {Title} from '@angular/platform-browser';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {Features} from '../services/auth/features';

const LEVEL_PRIORITY: Record<string, number> = {
    [level.PRODUCTION]: 1,
    [level.HEALTH]: 2,
    [level.CHECK]: 3,
    [level.ACCEPTANCE]: 4,
};

const LEVEL_DATA_MAP = {
    [level.PRODUCTION]: (p: Platform) => ({
        count: p.checkpoint_production_count,
        errorCount: p.checkpoint_production_error_count,
        warningCount: p.checkpoint_production_warning_count,
        alertCount: p.checkpoint_production_postpone_count,
        lastInstance: p.checkpoint_production_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.HEALTH]: (p: Platform) => ({
        count: p.checkpoint_health_count,
        errorCount: p.checkpoint_health_error_count,
        warningCount: p.checkpoint_health_warning_count,
        alertCount: p.checkpoint_health_postpone_count,
        lastInstance: p.checkpoint_health_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.CHECK]: (p: Platform) => ({
        count: p.checkpoint_check_count,
        errorCount: p.checkpoint_check_error_count,
        warningCount: p.checkpoint_check_warning_count,
        alertCount: p.checkpoint_check_postpone_count,
        lastInstance: p.checkpoint_check_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.ACCEPTANCE]: (p: Platform) => ({
        count: p.checkpoint_acceptance_count,
        errorCount: p.checkpoint_acceptance_error_count,
        warningCount: p.checkpoint_acceptance_warning_count,
        alertCount: p.checkpoint_acceptance_postpone_count,
        lastInstance: p.checkpoint_acceptance_last_instance?.[0]?.updated_at ?? null,
    }),
};

@Component({
    selector: 'app-seire',
    templateUrl: './seire.component.html',
    styleUrls: ['./seire.component.scss'],
    imports: [
        NgbTooltip,
        RouterLink,
        PlatformLevelStatusPipe,
        BreadcrumbComponent,
        UrlPrefixStrippingPipe,
        HasFeaturePipe,
        SslValidUntilPipe,
        DaysFromNowPipe,
        MinDatePipe,
        DatePipe,
    ]
})
export class SeireComponent extends CodaltComponent implements OnInit, OnDestroy {
    readonly platforms = signal<Platform[]>(null);
    readonly servers = signal<Server[]>([]);
    levelEnums = Object.values(LevelEnum);

    readonly sortedPlatforms = computed(() => {
        const list = this.platforms();
        if (!list?.length) {
            return list;
        }

        return [...list].sort((a, b) => {
            const statusA = this.getWorstPlatformStatusPriority(a);
            const statusB = this.getWorstPlatformStatusPriority(b);
            if (statusA !== statusB) {
                return statusA - statusB;
            }
            return a.name.localeCompare(b.name); // tie-break alphabetically
        });
    });

    constructor(
        private platformService: PlatformService,
        private serverService: ServerService,
        private titleService: Title
    ) {
        super();
        this.crumbs = [
            {name: 'Seire', route: Routenames.seire}
        ];
        this.titleService.setTitle(`Seire ${this.environment.titleAppend}`);
    }

    ngOnInit() {
        this.subscriptions.add(
            this.platformService.getPlatformsLive().subscribe({
                next: (platforms) => {
                    this.platforms.set(platforms.map(p => ({...p})));
                },
                error: err => console.error(err)
            })
        );

        if (AuthorisationService.hasFeature(Features.Servers)) {
            this.subscriptions.add(
                this.serverService.getServers().subscribe(servers => {
                    this.servers.set(servers);
                })
            );
        }
    }

    getPlatformLevelStatusPriority(levelKey: string, platform: Platform): number {
        const data = LEVEL_DATA_MAP[levelKey]?.(platform);
        if (!data?.count) {
            return 5; // inactive / no checkpoints
        }

        const timeBehind = new Date();
        timeBehind.setDate(timeBehind.getDate() - 1);
        const lastInstance = data.lastInstance ? new Date(data.lastInstance) : null;

        if (data.errorCount !== 0 || (lastInstance !== null && lastInstance < timeBehind)) {
            return 1; // error
        }
        if (data.warningCount !== 0) {
            return 2; // warning
        }
        if (data.alertCount) {
            return 3; // alert
        }
        return 4; // ok
    }

    getWorstPlatformStatusPriority(platform: Platform): number {
        const scores = Object.keys(LEVEL_DATA_MAP).map(l => ({
            levelPriority: LEVEL_PRIORITY[l],
            statusPriority: this.getPlatformLevelStatusPriority(l, platform),
        }));
        const activeScores = scores.filter(s => s.statusPriority < 5); // 5 = inactive
        if (!activeScores.length) {
            return 999; // no active levels → sort to bottom
        }
        const worstStatus = Math.min(...activeScores.map(s => s.statusPriority));
        const bestLevelWithWorstStatus = Math.min(
            ...activeScores
                .filter(s => s.statusPriority === worstStatus)
                .map(s => s.levelPriority)
        );
        return worstStatus * 10 + bestLevelWithWorstStatus;
    }

    readonly isOlderThan2Days = (lastUpdate: Date | string) => {
        const last = new Date(lastUpdate);
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
        return last < twoDaysAgo;
    };
}
